import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faDollarSign, faWrench, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import RepairTypeBadge from './RepairTypeBadge';

/**
 * RepairCostDetailModal Component
 * Shows detailed breakdown of repair costs when a cost cell is clicked
 */
const RepairCostDetailModal = ({ isOpen, onClose, repairData }) => {
  if (!isOpen || !repairData) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatArea = (area) => {
    return `${parseFloat(area || 0).toFixed(2)} m²`;
  };

  // Use backend repair type/label as source of truth so table and modal match
  const getRepairType = () => {
    // Prefer repairLabel from new simulation engine (TU, OC, RE)
    const label = (repairData.repairLabel || '').toUpperCase();
    if (label === 'TU') return 'touchup';
    if (label === 'OC') return 'overcoat';
    if (label === 'RE') return 'recoat';

    // Then prefer repairType from backend (touchup, full)
    if (repairData.repairType) {
      const t = String(repairData.repairType).toLowerCase();
      if (t === 'touchup' || t === 'touch-up') return 'touchup';
      if (t === 'full') return 'recoat';
    }

    // Fallback: infer from processes only when backend did not provide type
    if (!repairData.repairProcessesA && !repairData.repairProcessesB && !repairData.repairProcessesC) {
      return repairData.repairType || 'unknown';
    }

    const allProcesses = [
      ...(repairData.repairProcessesA || []),
      ...(repairData.repairProcessesB || []),
      ...(repairData.repairProcessesC || [])
    ].map(p => (p.processName || p.name || '').toLowerCase());

    const hasWash = allProcesses.some(p => p.includes('wash'));
    const hasPrime = allProcesses.some(p => p.includes('prime'));
    const hasStripe = allProcesses.some(p => p.includes('stripe'));
    const hasMid = allProcesses.some(p => p.includes('mid'));
    const hasTop = allProcesses.some(p => p.includes('top'));

    if (hasWash && !hasPrime && !hasStripe && !hasMid && !hasTop) return 'wash';
    if (!hasPrime && !hasStripe && !hasMid && hasTop) return 'touchup';
    if (hasPrime || hasStripe || hasMid) return 'recoat';

    return repairData.repairType || 'unknown';
  };

  const repairType = getRepairType();

  // Get all unique processes
  const getAllProcesses = () => {
    const processes = new Map();
    
    [...(repairData.repairProcessesA || []), 
     ...(repairData.repairProcessesB || []), 
     ...(repairData.repairProcessesC || [])].forEach(p => {
      const name = p.processName || p.name || 'Unknown';
      if (!processes.has(name)) {
        processes.set(name, {
          name,
          sequenceOrder: p.sequenceOrder || 999
        });
      }
    });

    return Array.from(processes.values()).sort((a, b) => a.sequenceOrder - b.sequenceOrder);
  };

  const processes = getAllProcesses();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faDollarSign} className="text-2xl" />
              <div>
                <h2 className="text-xl font-bold">Repair Cost Details</h2>
                <p className="text-sm text-blue-100 mt-1">
                  {repairData.spanCode || repairData.spanName || `Element ${repairData.spanId}`} - Year {repairData.year}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Repair Type Summary */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Repair Type</p>
                <div className="flex items-center gap-2">
                  <RepairTypeBadge repairType={repairType} />
                  <span className="text-lg font-semibold text-gray-900">
                    {repairType === 'wash' ? 'Wash' :
                     repairType === 'touchup' ? 'Touch-up' :
                     repairType === 'overcoat' ? 'Overcoat' :
                     repairType === 'recoat' ? 'Recoat/Full Repair' : 'Unknown'}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Total Cost</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(repairData.totalCost)}
                </p>
              </div>
            </div>
          </div>

          {/* Processes Applied */}
          {processes.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon={faWrench} />
                Processes Applied
              </h3>
              <div className="space-y-2">
                {processes.map((process, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded p-3">
                    <span className="text-sm font-medium text-gray-700">{process.name}</span>
                    <span className="text-xs text-gray-500">Step {process.sequenceOrder}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cost Breakdown by Severity */}
          {(repairData.costA > 0 || repairData.costB > 0 || repairData.costC > 0) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon={faLayerGroup} />
                Cost Breakdown by Damage Severity
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {repairData.costA > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-xs text-blue-600 mb-1">Severity A (Minor)</p>
                    <p className="text-lg font-bold text-blue-900">{formatCurrency(repairData.costA)}</p>
                    {repairData.areaA && (
                      <p className="text-xs text-blue-600 mt-1">Area: {formatArea(repairData.areaA)}</p>
                    )}
                  </div>
                )}
                {repairData.costB > 0 && (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <p className="text-xs text-yellow-600 mb-1">Severity B (Moderate)</p>
                    <p className="text-lg font-bold text-yellow-900">{formatCurrency(repairData.costB)}</p>
                    {repairData.areaB && (
                      <p className="text-xs text-yellow-600 mt-1">Area: {formatArea(repairData.areaB)}</p>
                    )}
                  </div>
                )}
                {repairData.costC > 0 && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <p className="text-xs text-red-600 mb-1">Severity C (Severe)</p>
                    <p className="text-lg font-bold text-red-900">{formatCurrency(repairData.costC)}</p>
                    {repairData.areaC && (
                      <p className="text-xs text-red-600 mt-1">Area: {formatArea(repairData.areaC)}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Details */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {repairData.corrosionCategory && (
                <div>
                  <p className="text-gray-600">Corrosion Category</p>
                  <p className="font-medium text-gray-900">{repairData.corrosionCategory}</p>
                </div>
              )}
              {repairData.degradationPercent !== undefined && (
                <div>
                  <p className="text-gray-600">Degradation at Repair</p>
                  <p className="font-medium text-gray-900">{repairData.degradationPercent?.toFixed(2) || 0}%</p>
                </div>
              )}
              {repairData.yearsSinceRepair !== undefined && (
                <div>
                  <p className="text-gray-600">Years Since Last Repair</p>
                  <p className="font-medium text-gray-900">{repairData.yearsSinceRepair || 0}</p>
                </div>
              )}
              {repairData.repairTriggered && (
                <div>
                  <p className="text-gray-600">Repair Triggered</p>
                  <p className="font-medium text-green-600">Yes</p>
                </div>
              )}
            </div>
          </div>

          {/* Note about Mobilization */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              <strong>Note:</strong> Mobilization costs are calculated at the project level and are not included in individual span costs shown here.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RepairCostDetailModal;

